<script>
  import { onDestroy, tick, onMount } from "svelte";
  import Toasts from "./toast/Toasts.svelte";
  import { addToast } from "./toast/store";

  let isOpen = false;
  let chatContainer;
  let chatInstance = null;
  let isLoading = false;
  let hasError = false;
  let errorMessage = "";
  let mounted = false;
  let remoteGlobalStylesPromise = null;

  const getInitialMcp = () => {
    const raw = process.env.SVELTE_APP_MCP_SERVER || "acelink-mcp";
    if (raw.startsWith("/mcp/")) return raw;
    if (raw.startsWith("mcp/")) return "/" + raw;
    return `/mcp/${raw}`;
  };

  let selectedMcp = getInitialMcp();

  const ensureRemoteGlobalStyles = () => {
    if (!remoteGlobalStylesPromise) {
      remoteGlobalStylesPromise = import("App/Opportunities/count").catch(() => null);
    }
    return remoteGlobalStylesPromise;
  };

  const updateMcpEndpoint = async (mcp) => {
    if (selectedMcp !== mcp) {
      console.log("[aiChatWidget] MCP Endpoint changed from", selectedMcp, "to", mcp);
      selectedMcp = mcp;
      if (chatInstance) {
        console.log("[aiChatWidget] Destroying old chatInstance to force full re-initialization on next load");
        if (typeof chatInstance.$destroy === "function") {
          chatInstance.$destroy();
        }
        chatInstance = null;
        mounted = false;
      }
      if (isOpen) {
        console.log("[aiChatWidget] Chat is open, re-mounting immediately");
        await tick();
        loadChatModule();
      }
    }
  };

  const handleConfigUpdate = (event) => {
    console.log("[aiChatWidget] handleConfigUpdate received detail:", event.detail);
    const raw = event.detail?.mcpServer;
    if (raw) {
      let mcp;
      if (raw.startsWith("/mcp/")) {
        mcp = raw;
      } else if (raw.startsWith("mcp/")) {
        mcp = "/" + raw;
      } else {
        mcp = `/mcp/${raw}`;
      }
      updateMcpEndpoint(mcp);
    }
  };

  const fetchRuntimeConfig = async () => {
    try {
      const settingsUrl = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/common-config/settings`;
      const res = await fetch(settingsUrl);
      if (res.ok) {
        const data = await res.json();
        if (data && data.mcpServer) {
          const raw = data.mcpServer;
          let mcp;
          if (raw.startsWith("/mcp/")) {
            mcp = raw;
          } else if (raw.startsWith("mcp/")) {
            mcp = "/" + raw;
          } else {
            mcp = `/mcp/${raw}`;
          }
          updateMcpEndpoint(mcp);
        }
      }
    } catch (e) {
      console.warn("Failed to fetch runtime MCP config:", e);
    }
  };

  onMount(() => {
    ensureRemoteGlobalStyles();
    fetchRuntimeConfig();
    window.addEventListener("ai-config-updated", handleConfigUpdate);
  });

  // ── Toggle: panel is always in DOM, so chatContainer ref is always valid.
  const toggleChat = () => {
    isOpen = !isOpen;
    if (isOpen && !mounted) {
      loadChatModule();
    }
  };

  // ── Close
  const closeChat = () => {
    isOpen = false;
  };

  const showHostToastIfRemoteMissing = (message) => {
    setTimeout(() => {
      const remoteToastVisible = Array.from(document.body.querySelectorAll("div, span"))
        .some((element) => element.textContent?.trim() === message);

      if (!remoteToastVisible) {
        addToast({ message, type: "success" });
      }
    }, 150);
  };

  const handleChatClick = (event) => {
    const button = event.target?.closest?.("button");
    const label = button?.textContent?.replace(/\s+/g, " ").trim();

    if (label === "Save Settings") {
      showHostToastIfRemoteMissing("Settings saved.");
    } else if (label === "Clear") {
      showHostToastIfRemoteMissing("API key cleared.");
    }
  };

  // ── Load Remote Module
  const loadChatModule = async () => {
    if (mounted || isLoading) return;

    isLoading = true;
    hasError = false;

    try {
      await ensureRemoteGlobalStyles();
      await tick();
      const module = await import("App/AI-Chat");
      const ChatComponent = module.default;

      if (!ChatComponent) throw new Error("default export is undefined");

      isLoading = false;
      await tick();

      if (!chatContainer) throw new Error("chatContainer is null after tick");

      chatContainer.innerHTML = "";
      chatInstance = new ChatComponent({
        target: chatContainer,
        props: {
          mcpEndpoint: selectedMcp,
          override: true,
          additionalMcpGuidelines: {
            "/mcp/mcp-aws-apn": `You have access to an AWS Partner Central AI agent via the \`sendMessage\` tool.

IMPORTANT RULES — follow these for every user message:
1. **Always call \`sendMessage\`** for any user question or request. Never attempt to answer Partner Central questions (opportunities, funding, sales strategies, engagement scores) from your own knowledge.
2. Pass the user's message verbatim in the \`content\` array as \`{ type: "text", text: "<user message>" }\`.
3. After \`sendMessage\` returns, present its response directly to the user — do not add your own commentary unless something is genuinely unclear.
4. Use \`getSession\` only when the user explicitly asks to review session history or check a pending approval — never as a substitute for \`sendMessage\`.`,
            "/mcp/acelink-plus-aws-apn": `You have access to an AWS Partner Central AI agent via the \`sendMessage\` tool.

IMPORTANT RULES — follow these for every user message:
1. **Always call \`sendMessage\`** for any user question or request. Never attempt to answer Partner Central questions (opportunities, funding, sales strategies, engagement scores) from your own knowledge.
2. Pass the user's message verbatim in the \`content\` array as \`{ type: "text", text: "<user message>" }\`.
3. After \`sendMessage\` returns, present its response directly to the user — do not add your own commentary unless something is genuinely unclear.
4. Use \`getSession\` only when the user explicitly asks to review session history or check a pending approval — never as a substitute for \`sendMessage\`.`,
          },
        }
      });
      mounted = true;
    } catch (err) {
      hasError = true;
      errorMessage = err?.message ?? "Failed to load chat module.";
      isLoading = false;
    }
  };

  // ── Retry
  const retryLoad = async () => {
    mounted = false;
    hasError = false;
    errorMessage = "";
    await tick();
    loadChatModule();
  };

  // ── Lifecycle
  onDestroy(() => {
    window.removeEventListener("ai-config-updated", handleConfigUpdate);
    if (chatInstance && typeof chatInstance.$destroy === "function") {
      chatInstance.$destroy();
    }
  });
</script>

<!-- ── Floating Action Button ──────────────────────────────────── -->
<button
  id="ai-chat-toggle-btn"
  class="fixed bottom-5 right-5 z-[910] w-14 h-14 rounded-full border-0 cursor-pointer sm:bottom-7 sm:right-7
         flex items-center justify-center text-white
         bg-[#34344A]
         shadow-[0_4px_20px_rgba(52,52,74,0.7),0_2px_8px_rgba(0,0,0,0.3),0_0_0_2px_rgba(255,255,255,0.28)]
         transition-all duration-150 hover:scale-105 hover:bg-[#3e3e58] hover:shadow-[0_6px_28px_rgba(52,52,74,0.9),0_0_0_1px_rgba(255,255,255,0.22)]"
  on:click={toggleChat}
  aria-label={isOpen ? "Close AI Chat" : "Open AI Chat"}
  title={isOpen ? "Close Chat" : "AI Assistant"}
>
  {#if isOpen}
    <!-- Close icon -->
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" stroke-width="2.5"
         stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  {:else}
    <!-- Chat bubble icon -->
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
         fill="none" stroke="currentColor" stroke-width="2"
         stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
    </svg>
  {/if}
</button>

<!-- ── Backdrop: always in DOM, hidden when closed ────────────── -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="fixed inset-0 z-[880] bg-black/30"
  on:click={closeChat}
  aria-hidden="true"
  style:display={isOpen ? "block" : "none"}
></div>

<!-- ── Chat Panel: always in DOM, toggled via display ─────────── -->
<div
  id="ai-chat-panel"
  role="dialog"
  aria-label="AI Chat Assistant"
  style:display={isOpen ? "grid" : "none"}
  style="height: min(800px, calc(100dvh - 130px)); width: min(1000px, calc(100vw - 24px)); grid-template-rows: auto 1fr;"
  class="fixed bottom-20 right-3 z-[900] sm:bottom-24 sm:right-7
         rounded-2xl overflow-hidden
         bg-[#1e2233]
         shadow-[0_24px_64px_rgba(0,0,0,0.4),0_8px_24px_rgba(52,52,74,0.3),0_0_0_1px_rgba(255,255,255,0.06)]"
>
  <!-- Header -->
  <div class="chat-header flex items-center justify-between px-4 py-3
              text-white flex-shrink-0">
    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm
                  border border-white/30 flex items-center justify-center flex-shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83
                   M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
        </svg>
      </div>

      <div class="flex flex-col">
        <span class="font-bold text-[15px] tracking-tight leading-tight font-sans">
          AI Assistant
        </span>
        <span class="flex items-center gap-1.5 text-[11px] opacity-85 mt-0.5">
          <span class="w-1.5 h-1.5 rounded-full bg-green-400
                       shadow-[0_0_0_2px_rgba(74,222,128,0.3)] animate-pulse inline-block"></span>
          Online
        </span>
      </div>
    </div>

    <button
      class="w-8 h-8 rounded-full bg-white/15 border-0 text-white cursor-pointer
             flex items-center justify-center flex-shrink-0
             transition-colors duration-200 hover:bg-white/25"
      on:click={closeChat}
      aria-label="Close chat"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2.5"
           stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </div>

  <!-- Body: fills the second grid row, constrains remote component height -->
  <div class="flex flex-col bg-[#1e2233] relative" style="min-height: 0; overflow: hidden;">
    <!-- Loading overlay -->
    {#if isLoading}
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3
                  text-white/70 bg-[#1e2233] z-10">
        <div class="w-9 h-9 rounded-full border-[3px] border-white/20 border-t-white animate-spin"></div>
        <p class="text-sm font-medium font-sans">Loading AI Chat…</p>
      </div>
    {/if}

    <!-- Error overlay -->
    {#if hasError}
      <div class="absolute inset-0 flex flex-col items-center justify-center gap-3
                  p-6 text-center bg-[#1e2233] z-10">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
             fill="none" stroke="#ef4444" stroke-width="1.5"
             stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p class="font-semibold text-sm text-white/90 m-0">Could not load the chat module.</p>
        <small class="text-white/50 text-[11px] break-words">{errorMessage}</small>
        <button
          class="mt-2 px-5 py-2 rounded-lg border-0 cursor-pointer
                 bg-[#34344A] border border-white/20
                 text-white text-[13px] font-semibold
                 transition-all duration-200 hover:bg-[#3e3e58]"
          on:click={retryLoad}
        >
          Try Again
        </button>
      </div>
    {/if}

    <!-- Mount target: always in DOM, fills all remaining body space -->
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      id="ai-chat-mount"
      bind:this={chatContainer}
      on:click={handleChatClick}
      style="flex: 1; min-height: 0; display: flex; flex-direction: column; overflow: hidden;"
    ></div>
  </div>
</div>

<Toasts />

<style>
  #ai-chat-panel {
    background: #1e2233 !important;
    border: 1px solid rgba(226, 232, 240, 0.12);
  }

  .chat-header {
    background: #24283a;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  #ai-chat-panel :global(.chat-body-surface),
  #ai-chat-panel > div {
    background-color: #1e2233;
  }

  #ai-chat-toggle-btn { z-index: 900; }
  #ai-chat-panel { z-index: 950; }
  @media (max-width: 640px) {
    #ai-chat-toggle-btn { width: 3rem !important; height: 3rem !important; bottom: 1rem !important; right: 1rem !important; }
    #ai-chat-panel { bottom: 4.5rem; right: 0.75rem; left: 0.75rem; width: auto !important; }
  }

  /* Force the remote chat component to fill the mount container fully,
     so its internal flex layout can pin the input bar to the bottom. */
  :global(#ai-chat-mount > *) {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100% !important;
  }

  #ai-chat-toggle-btn {
    border-radius: 9999px !important;
    background: #34344a !important;
    outline: 2px solid rgba(255, 255, 255, 0.28);
    outline-offset: 0;
  }

  #ai-chat-toggle-btn:hover {
    background: #3e3e58 !important;
    outline-color: rgba(255, 255, 255, 0.36);
  }

  :global(#ai-chat-mount) {
    color: #f1f5f9;
  }

  :global(#ai-chat-mount [class*="bg-primary"]) {
    background-color: #2080df !important;
    color: #fff !important;
  }

  :global(#ai-chat-mount [class*="text-primary-fg"]) {
    color: #fff !important;
  }

  :global(#ai-chat-mount [class*="from-primary"]) {
    --tw-gradient-from: #2080df var(--tw-gradient-from-position);
    --tw-gradient-to: rgb(32 128 223 / 0) var(--tw-gradient-to-position);
    --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
  }

  :global(#ai-chat-mount [class*="to-[#1a6bc4]"]) {
    --tw-gradient-to: #1a6bc4 var(--tw-gradient-to-position);
  }

  #ai-chat-mount,
  :global(#ai-chat-mount > *) {
    background-color: #1e2233 !important;
    background-image: none !important;
  }

  :global(#ai-chat-mount [class*="bg-white"]),
  :global(#ai-chat-mount [class*="bg-stone-"]),
  :global(#ai-chat-mount [class*="bg-gray-"]) {
    background-color: #24283a !important;
    background-image: none !important;
  }

  :global(#ai-chat-mount [class*="text-stone-"]),
  :global(#ai-chat-mount [class*="text-gray-"]),
  :global(#ai-chat-mount [class*="text-slate-"]) {
    color: #dbe3f0 !important;
  }

  :global(#ai-chat-mount .ai-chat-settings svg),
  :global(#ai-chat-mount .mcp-panel svg) {
    color: #60a5fa !important;
    opacity: 1 !important;
    stroke: currentColor !important;
  }

  :global(#ai-chat-mount [class*="border-stone-"]),
  :global(#ai-chat-mount [class*="border-gray-"]) {
    border-color: rgba(226, 232, 240, 0.14) !important;
  }

  :global(#ai-chat-mount input),
  :global(#ai-chat-mount textarea),
  :global(#ai-chat-mount select) {
    background-color: #1f2334 !important;
    color: #f8fafc !important;
  }
</style>
